import { useTranslation } from "react-i18next";
import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { SubmitHandler, useForm } from "react-hook-form";
import { useCreateBillingEntity } from "@/api/billingEntity.api";
import { Modal } from "@/components/ui/customs/modal";
import { Button } from "@/components/ui/button";
import { BillingEntityForm } from "@/components/shared/billingEntity/BillingEntityForm";
import {
  ProfileBillingEntityFormData,
  profileBillingEntitySchema,
  DEFAULT_PROFILE_BILLING_ENTITY_VALUES,
} from "@/types/forms/shared/billingEntity.form";

type GeneralInfoModalCompanyProps = {
  closeModal: () => void;
};

export const GeneralInfoModalCreateBillingEntity = ({
  closeModal,
}: GeneralInfoModalCompanyProps) => {
  const { t } = useTranslation(["boClients", "common"]);
  const [canSubmit, setCanSubmit] = useState(false);
  const { mutateAsync: createBillingEntity, isPending } = useCreateBillingEntity();

  const billingEntityForm = useForm<ProfileBillingEntityFormData>({
    resolver: zodResolver(profileBillingEntitySchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: DEFAULT_PROFILE_BILLING_ENTITY_VALUES,
  });

  const onSubmit: SubmitHandler<ProfileBillingEntityFormData> = async (data) => {
    const ret = await createBillingEntity(data);
    if (ret?.status === 200 || ret?.status === 201) {
      closeModal();
    }
  };

  return (
    <Modal
      open={true}
      onClose={closeModal}
      title={t("profileCreateBillingEntityModal.title")}
      size="md"
      footer={
        <div className="flex w-full gap-2">
          <Button
            type="submit"
            form="create-billing-entity-form"
            className="w-full"
            disabled={isPending || !canSubmit}
          >
            {t("profileCreateBillingEntityModal.submit")}
          </Button>
          <Button type="button" variant="secondary" onClick={closeModal} className="w-full">
            {t("common:actions.cancel")}
          </Button>
        </div>
      }
    >
      <form
        id="create-billing-entity-form"
        onSubmit={billingEntityForm.handleSubmit(onSubmit)}
        className="flex w-full flex-col gap-3"
      >
        <BillingEntityForm
          control={billingEntityForm.control}
          showIsDefault
          setValue={billingEntityForm.setValue}
          onCanSubmitChange={setCanSubmit}
        />
      </form>
    </Modal>
  );
};
